import { useState } from 'react'
import { useApp } from '@/store/AppContext'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { formatCurrency } from '@/lib/pricing'
import {
  Users,
  UserX,
  Banknote,
  Plus,
  CreditCard,
  History,
  Edit,
  MessageSquare,
  QrCode,
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { SubscriberForm } from '@/components/subscribers/SubscriberForm'
import { SubscriberPaymentDialog } from '@/components/subscribers/SubscriberPaymentDialog'
import { SubscriberHistoryDialog } from '@/components/subscribers/SubscriberHistoryDialog'

export default function Subscribers() {
  const { subscribers, subscriberPayments } = useApp()

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingSub, setEditingSub] = useState<any>(null)
  const [paymentSubId, setPaymentSubId] = useState<string | null>(null)
  const [historySubId, setHistorySubId] = useState<string | null>(null)

  const activeCount = subscribers.filter((s) => s.status === 'Ativo').length
  const overdueCount = subscribers.filter(
    (s) => s.status === 'Inadimplente',
  ).length
  const projectedRevenue = subscribers
    .filter((s) => s.status !== 'Suspenso')
    .reduce((sum, s) => sum + s.monthlyFee, 0)

  const currentMonth = new Date().toISOString().slice(0, 7)
  const receivedThisMonth = subscriberPayments
    .filter((p) => p.referenceMonth === currentMonth)
    .reduce((sum, p) => sum + p.amount, 0)

  return (
    <div className="space-y-6 animate-fade-in-up pb-10 h-full flex flex-col">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 flex-shrink-0">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Gestão de Mensalistas
          </h1>
          <p className="text-muted-foreground">
            Controle de assinaturas, pagamentos e status de clientes
            recorrentes.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditingSub(null)
            setIsFormOpen(true)
          }}
          className="font-bold shadow-sm w-full md:w-auto"
        >
          <Plus className="h-4 w-4 mr-2" /> Novo Mensalista
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 flex-shrink-0">
        <Card className="shadow-sm border-l-4 border-l-emerald-500">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Mensalistas Ativos
            </CardTitle>
            <Users className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-black">{activeCount}</div>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-l-4 border-l-amber-500">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Inadimplentes
            </CardTitle>
            <UserX className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-black">{overdueCount}</div>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-l-4 border-l-blue-500">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Receita Prevista
            </CardTitle>
            <Banknote className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-black">
              {formatCurrency(projectedRevenue)}
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-l-4 border-l-primary">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Recebida (Mês Atual)
            </CardTitle>
            <Banknote className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-black">
              {formatCurrency(receivedThisMonth)}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-sm flex-1 overflow-hidden flex flex-col">
        <CardContent className="p-0 overflow-auto flex-1">
          <Table>
            <TableHeader className="bg-muted/50 sticky top-0 z-10">
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Placa(s)</TableHead>
                <TableHead>Vencimento</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right pr-6">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscribers.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center h-24 text-muted-foreground"
                  >
                    Nenhum mensalista cadastrado.
                  </TableCell>
                </TableRow>
              ) : (
                subscribers.map((sub) => (
                  <TableRow key={sub.id} className="group">
                    <TableCell>
                      <div className="font-bold whitespace-nowrap">
                        {sub.name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {sub.phone || sub.email || 'Sem contato'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[150px]">
                        {sub.plates.map((p) => (
                          <Badge
                            key={p}
                            variant="secondary"
                            className="text-xs font-mono"
                          >
                            {p}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap font-medium">
                      Dia {sub.dueDate}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          sub.status === 'Ativo'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : sub.status === 'Inadimplente'
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : 'bg-red-50 text-red-700 border-red-200'
                        }
                      >
                        {sub.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right pr-6">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="default"
                          className="h-8 shadow-sm text-xs font-bold"
                          onClick={() => setPaymentSubId(sub.id)}
                        >
                          <CreditCard className="h-3 w-3 mr-1" /> Pagar
                        </Button>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-8 w-8 text-muted-foreground"
                              disabled
                            >
                              <MessageSquare className="h-3 w-3" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            Lembrete via WhatsApp (Em breve)
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-8 w-8 text-muted-foreground"
                              disabled
                            >
                              <QrCode className="h-3 w-3" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            Gerar Pix Copia e Cola (Em breve)
                          </TooltipContent>
                        </Tooltip>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => setHistorySubId(sub.id)}
                        >
                          <History className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => {
                            setEditingSub(sub)
                            setIsFormOpen(true)
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <SubscriberForm
        open={isFormOpen}
        onClose={() => {
          setIsFormOpen(false)
          setEditingSub(null)
        }}
        subscriber={editingSub}
      />
      <SubscriberPaymentDialog
        subscriberId={paymentSubId}
        onClose={() => setPaymentSubId(null)}
      />
      <SubscriberHistoryDialog
        subscriberId={historySubId}
        onClose={() => setHistorySubId(null)}
      />
    </div>
  )
}
